import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Container,
  LoadingOverlay,
  PasswordInput,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';

import { isApiError } from '@api/index';
import {
  useCheckTokenQuery,
  usePasswordResetMutation,
} from '@api/passwordReset.api';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useGRecaptcha from '@hooks/useGRecaptcha';

export default function PasswordReset() {
  const { t } = useTranslation();

  // ==========================================================================
  // General
  // ==========================================================================
  const [params, setParams] = useQueryParams();

  const { getRecaptchaToken } = useGRecaptcha();

  const token = useMemo(() => params.get('t'), [params]);

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    password: '',
    passwordConfirm: '',
  };

  const form = useForm({
    initialValues,
    validate: {
      passwordConfirm: (value, values) =>
        value !== values.password ? t('passwordReset.passwordMismatch') : null,
    },
  });

  const onSubmit = async (values: typeof initialValues) => {
    try {
      const recaptchaToken = await getRecaptchaToken('PasswordReset');

      await passwordReset({
        token: token!,
        body: {
          password: values.password,
        },
        recaptchaToken,
      }).unwrap();

      params.set('p', 'login');
      params.set('alert', t('passwordReset.passwordResetSuccess'));
      setParams(params);
    } catch (e) {
      if (isApiError(e)) {
        if (e.status === 400) {
          form.setErrors({ general: e.data.message, ...e.data.errors });
        } else if (e.status === 404) {
          // TODO
        }
      } else {
        form.setErrors({
          general: t('passwordReset.unexpectedError'),
        });
      }
    }
  };

  // ==========================================================================
  // Api
  // ==========================================================================
  const { isError, isLoading: isLoadingCheckToken } = useCheckTokenQuery(
    token!,
  );

  const [passwordReset, { isLoading }] = usePasswordResetMutation();

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container w="100%" size="40rem">
      <LoadingOverlay visible={isLoadingCheckToken} />

      <Text ta="center" fw="bold" size="1.5rem">
        {t('passwordReset.title')}
      </Text>

      <Text ta="center" mt="md">
        {t('passwordReset.alreadyKnowPassword')}{' '}
        <Text
          span
          c="brand"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            params.set('p', 'login');
            setParams(params);
          }}
        >
          {t('passwordReset.signIn')}
        </Text>
      </Text>

      {isError ? (
        <>
          <Text mt="lg" ta="center">
            {t('passwordReset.invalidToken')}
          </Text>
          <Text
            ta="center"
            c="brand"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              params.set('p', 'password-reset-request');
              setParams(params);
            }}
          >
            {t('passwordReset.requestNewLink')}
          </Text>
        </>
      ) : (
        <form
          onSubmit={form.onSubmit((values) => {
            onSubmit(values);
          })}
        >
          <Text ta="center" mb="lg" mt="xl">
            {t('passwordReset.newPasswordDescription')}
          </Text>

          <PasswordInput
            label={t('passwordReset.newPassword')}
            placeholder={t('passwordReset.newPasswordPlaceholder')}
            required
            {...form.getInputProps('password')}
          />
          <PasswordInput
            mt="md"
            label={t('passwordReset.repeatPassword')}
            placeholder={t('passwordReset.repeatPasswordPlaceholder')}
            required
            {...form.getInputProps('passwordConfirm')}
          />

          {form.errors.general && (
            <Text ta="center" c="red" size="sm" mt="xl">
              {form.errors.general}
            </Text>
          )}
          <Button
            type="submit"
            fullWidth
            mt="xl"
            style={{ display: 'block' }}
            loading={isLoading}
          >
            {t('passwordReset.confirm')}
          </Button>
        </form>
      )}
    </Container>
  );
}
